import React, { useEffect, useState, useCallback } from 'react';
import {
  LogOut, Users, Mail, Search, Download, Trash2, RefreshCw,
  Loader2, Eye, EyeOff, CheckCircle2, Circle,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const AdminDashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState('subscribers');
  const [stats, setStats] = useState({ subscribers: 0, contacts: 0 });
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedMsg, setExpandedMsg] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;
  const authHeader = { Authorization: `Bearer ${token}` };

  const handleAuthFailure = useCallback(() => {
    localStorage.removeItem('bmp_admin_token');
    localStorage.removeItem('bmp_admin_expires');
    onLogout();
  }, [onLogout]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, { headers: authHeader });
      if (res.status === 401) return handleAuthFailure();
      if (res.ok) setStats(await res.json());
    } catch (e) { /* ignore */ }
  }, [API, token, handleAuthFailure]);

  const fetchSubscribers = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const url = `${API}/api/admin/subscribers${q ? `?q=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { headers: authHeader });
      if (res.status === 401) return handleAuthFailure();
      const data = await res.json();
      setSubscribers(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [API, token, handleAuthFailure]);

  const fetchContacts = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const url = `${API}/api/admin/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { headers: authHeader });
      if (res.status === 401) return handleAuthFailure();
      const data = await res.json();
      setContacts(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [API, token, handleAuthFailure]);

  // Initial load + on tab change, refresh data
  useEffect(() => {
    fetchStats();
    if (tab === 'subscribers') fetchSubscribers();
    else fetchContacts();
    setQuery('');
  }, [tab, fetchStats, fetchSubscribers, fetchContacts]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (tab === 'subscribers') fetchSubscribers(query);
    else fetchContacts(query);
  };

  const refresh = () => {
    fetchStats();
    if (tab === 'subscribers') fetchSubscribers(query);
    else fetchContacts(query);
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm('Delete this subscriber? This cannot be undone.')) return;
    const res = await fetch(`${API}/api/admin/subscribers/${id}`, { method: 'DELETE', headers: authHeader });
    if (res.ok) {
      setSubscribers((s) => s.filter((x) => x.id !== id));
      setStats((st) => ({ ...st, subscribers: Math.max(0, st.subscribers - 1) }));
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    const res = await fetch(`${API}/api/admin/contacts/${id}`, { method: 'DELETE', headers: authHeader });
    if (res.ok) {
      setContacts((s) => s.filter((x) => x.id !== id));
      setStats((st) => ({ ...st, contacts: Math.max(0, st.contacts - 1) }));
    }
  };

  const toggleContactStatus = async (msg) => {
    const newStatus = msg.status === 'read' ? 'new' : 'read';
    const res = await fetch(`${API}/api/admin/contacts/${msg.id}/status`, {
      method: 'PATCH',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (res.ok) {
      setContacts((s) => s.map((x) => (x.id === msg.id ? { ...x, status: newStatus } : x)));
    }
  };

  const csvUrl = (kind) => {
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    return `${API}/api/admin/export/${kind}.csv?token=${encodeURIComponent(token)}${q}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-slate-100">
      {/* Header */}
      <header className="border-b border-slate-800/80 bg-slate-950/70 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-light tracking-wide">Field Keeper</h1>
            <p className="text-xs text-slate-500">
              {stats.subscribers} subscribers · {stats.contacts} messages
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              aria-label="Refresh"
              data-testid="admin-refresh-btn"
              className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-slate-800/60 hover:bg-slate-700/70 border border-slate-700/60 text-slate-400 hover:text-slate-100 transition-all duration-300"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/60 hover:bg-slate-700/70 border border-slate-700/60 text-slate-300 hover:text-slate-100 text-sm font-light transition-all duration-300"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab('subscribers')}
            data-testid="admin-tab-subscribers"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-light transition-all duration-300 ${
              tab === 'subscribers'
                ? 'bg-gradient-to-r from-blue-500/30 to-teal-500/30 border border-blue-400/50 text-white'
                : 'bg-slate-800/40 border border-slate-700/50 text-slate-400 hover:text-slate-200'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Subscribers</span>
            <span className="text-xs opacity-70">({stats.subscribers})</span>
          </button>
          <button
            onClick={() => setTab('contacts')}
            data-testid="admin-tab-contacts"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-light transition-all duration-300 ${
              tab === 'contacts'
                ? 'bg-gradient-to-r from-blue-500/30 to-teal-500/30 border border-blue-400/50 text-white'
                : 'bg-slate-800/40 border border-slate-700/50 text-slate-400 hover:text-slate-200'
            }`}
          >
            <Mail className="w-4 h-4" />
            <span>Contact messages</span>
            <span className="text-xs opacity-70">({stats.contacts})</span>
          </button>
        </div>

        {/* Controls row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === 'subscribers' ? 'Search email or name…' : 'Search name, email, subject, message…'}
              data-testid="admin-search-input"
              className="w-full pl-11 pr-4 py-2.5 bg-slate-800/50 border border-slate-700/60 focus:border-blue-400/60 rounded-full text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300 text-sm"
            />
          </form>
          <a
            href={csvUrl(tab === 'subscribers' ? 'subscribers' : 'contacts')}
            data-testid="admin-export-csv"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-800/60 hover:bg-slate-700/70 border border-blue-400/40 hover:border-blue-300/70 rounded-full text-blue-100 hover:text-white text-sm font-light transition-all duration-300 whitespace-nowrap"
          >
            <Download className="w-4 h-4" />
            <span>Download CSV</span>
          </a>
        </div>

        {/* Data area */}
        <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="py-20 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="font-light text-sm">Loading…</span>
            </div>
          ) : tab === 'subscribers' ? (
            <SubscribersTable items={subscribers} onDelete={deleteSubscriber} />
          ) : (
            <ContactsTable
              items={contacts}
              onDelete={deleteContact}
              onToggleStatus={toggleContactStatus}
              expandedMsg={expandedMsg}
              setExpandedMsg={setExpandedMsg}
            />
          )}
        </div>
      </main>
    </div>
  );
};

const SubscribersTable = ({ items, onDelete }) => {
  if (items.length === 0) {
    return <EmptyState message="No subscribers yet. As people join The Field, they'll appear here." />;
  }
  return (
    <div className="overflow-x-auto" data-testid="subscribers-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700/50 text-xs uppercase tracking-wider text-slate-500">
            <th className="text-left px-5 py-3 font-light">Email</th>
            <th className="text-left px-5 py-3 font-light">Name</th>
            <th className="text-left px-5 py-3 font-light">Joined</th>
            <th className="text-left px-5 py-3 font-light">Status</th>
            <th className="text-left px-5 py-3 font-light">Brevo</th>
            <th className="text-right px-5 py-3 font-light">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
              <td className="px-5 py-3 text-slate-100 font-light break-all">{s.email}</td>
              <td className="px-5 py-3 text-slate-300 font-light">{s.name || '—'}</td>
              <td className="px-5 py-3 text-slate-400 font-light whitespace-nowrap">{formatDate(s.subscribed_at)}</td>
              <td className="px-5 py-3 text-slate-400 font-light capitalize">{s.status || 'active'}</td>
              <td className="px-5 py-3 font-light">
                {s.brevo_synced ? (
                  <span className="inline-flex items-center gap-1 text-teal-300 text-xs">
                    <CheckCircle2 className="w-3 h-3" /> synced
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-slate-500 text-xs">
                    <Circle className="w-3 h-3" /> local only
                  </span>
                )}
              </td>
              <td className="px-5 py-3 text-right">
                <button
                  onClick={() => onDelete(s.id)}
                  aria-label="Delete"
                  data-testid={`subscriber-delete-${s.id}`}
                  className="p-1.5 rounded-full text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ContactsTable = ({ items, onDelete, onToggleStatus, expandedMsg, setExpandedMsg }) => {
  if (items.length === 0) {
    return <EmptyState message="No contact messages yet." />;
  }
  return (
    <div className="overflow-x-auto" data-testid="contacts-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700/50 text-xs uppercase tracking-wider text-slate-500">
            <th className="text-left px-5 py-3 font-light w-8"></th>
            <th className="text-left px-5 py-3 font-light">From</th>
            <th className="text-left px-5 py-3 font-light">Subject / Message</th>
            <th className="text-left px-5 py-3 font-light">Sent</th>
            <th className="text-right px-5 py-3 font-light">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => {
            const isExpanded = expandedMsg === m.id;
            const isRead = m.status === 'read';
            return (
              <tr
                key={m.id}
                className={`border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors ${
                  isRead ? 'opacity-60' : ''
                }`}
              >
                <td className="px-5 py-3">
                  <button
                    onClick={() => onToggleStatus(m)}
                    aria-label={isRead ? 'Mark unread' : 'Mark read'}
                    className="text-slate-500 hover:text-blue-300 transition-colors"
                    title={isRead ? 'Mark unread' : 'Mark read'}
                  >
                    {isRead ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </td>
                <td className="px-5 py-3 text-slate-100 font-light">
                  <div>{m.name}</div>
                  <div className="text-xs text-slate-400 break-all">{m.email}</div>
                </td>
                <td className="px-5 py-3 text-slate-300 font-light max-w-xl">
                  <div className="font-normal text-slate-200">{m.subject}</div>
                  <button
                    onClick={() => setExpandedMsg(isExpanded ? null : m.id)}
                    className="mt-1 text-left text-slate-400 hover:text-slate-200 text-xs leading-relaxed"
                  >
                    {isExpanded
                      ? m.message
                      : `${(m.message || '').slice(0, 120)}${(m.message || '').length > 120 ? '…' : ''}`}
                  </button>
                </td>
                <td className="px-5 py-3 text-slate-400 font-light whitespace-nowrap">{formatDate(m.sent_at)}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => onDelete(m.id)}
                    aria-label="Delete"
                    className="p-1.5 rounded-full text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="py-20 text-center text-slate-500 font-light text-sm px-6">{message}</div>
);

export default AdminDashboard;
